import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  Field,
  Select,
  TextArea,
  formatDate,
} from "@odingard/design-system";
import type {
  ComplianceFramework,
  FrameworkRequirement,
  FrameworkVersion,
  MappingMethod,
  MappingState,
  Policy,
  RequirementMapping,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { frameworksApi, policyApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import { MappingStateBadge, methodLabel } from "./labels";

interface Options {
  versions: { version: FrameworkVersion; framework: ComplianceFramework }[];
  policies: Policy[];
}

const STATES: MappingState[] = ["proposed", "verified", "rejected", "stale", "superseded"];
const METHODS: MappingMethod[] = ["manual", "ai_suggested", "imported", "derived"];

// Requirement mappings and their governed decisions (APP-004 R3 P1).
//
// A mapping's state is only ever changed through the backend's decision route,
// and the immutable history is displayed as returned. The UI does not decide
// what a decision means for coverage — the next coverage read does.
export function GovMappings() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const versionId = params.get("frameworkVersionId") ?? "";
  const requirementId = params.get("requirementId") ?? "";
  const stateFilter = params.get("state") ?? "";
  const methodFilter = params.get("method") ?? "";
  const [history, setHistory] = useState<RequirementMapping | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === "frameworkVersionId") next.delete("requirementId");
    setParams(next);
  };

  const options = useAsync<Options>(async () => {
    const frameworks = (await frameworksApi.frameworks(orgId, { scope: "tenant" })).data.frameworks;
    const versions = (
      await Promise.all(
        frameworks.map(async (framework) =>
          (await frameworksApi.versions(orgId, framework.id)).data.versions.map((version) => ({
            version,
            framework,
          })),
        ),
      )
    ).flat();
    const policies = (await policyApi.policies(orgId)).data.policies;
    return { versions, policies };
  }, [orgId]);

  const requirements = useAsync<FrameworkRequirement[]>(
    async () =>
      versionId ? (await frameworksApi.requirements(orgId, versionId)).data.requirements : [],
    [orgId, versionId],
  );

  const mappings = useAsync(
    async () =>
      (
        await frameworksApi.mappings(orgId, {
          ...(versionId ? { frameworkVersionId: versionId } : {}),
          ...(requirementId ? { requirementId } : {}),
          ...(stateFilter ? { state: stateFilter as MappingState } : {}),
          ...(methodFilter ? { method: methodFilter as MappingMethod } : {}),
        })
      ).data.mappings,
    [orgId, versionId, requirementId, stateFilter, methodFilter],
  );

  const requirementLabel = (id: string) => {
    const requirement = requirements.data?.find((r) => r.id === id);
    return requirement ? `${requirement.requirementKey} ${requirement.title}` : id;
  };
  const policyLabel = (id: string | null) => {
    const policy = options.data?.policies.find((p) => p.id === id);
    return policy ? policy.title : (id ?? "Unknown policy");
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Mappings</h1>
          <p>
            Policies mapped to framework requirements. Only mappings a person has verified count towards coverage,
            an AI suggestion is a proposal until someone decides on it.
          </p>
        </div>
        <div className="badge-row">
          {versionId && requirements.data && requirements.data.length > 0 ? (
            <>
              <SuggestMappings
                orgId={orgId}
                requirements={requirements.data}
                initialRequirementId={requirementId}
                onDone={mappings.reload}
              />
              <CreateMapping
                orgId={orgId}
                requirements={requirements.data}
                policies={options.data?.policies ?? []}
                initialRequirementId={requirementId}
                onDone={mappings.reload}
              />
            </>
          ) : null}
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="filter-row">
            <Field label="Framework version">
              {(id) => (
                <Select
                  id={id}
                  value={versionId}
                  onChange={(e) => setParam("frameworkVersionId", e.target.value)}
                >
                  <option value="">All versions</option>
                  {(options.data?.versions ?? []).map(({ version, framework }) => (
                    <option key={version.id} value={version.id}>
                      {framework.name} {version.versionLabel}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Requirement">
              {(id) => (
                <Select
                  id={id}
                  value={requirementId}
                  disabled={!versionId}
                  onChange={(e) => setParam("requirementId", e.target.value)}
                >
                  <option value="">All requirements</option>
                  {(requirements.data ?? []).map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.requirementKey}: {r.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="State">
              {(id) => (
                <Select id={id} value={stateFilter} onChange={(e) => setParam("state", e.target.value)}>
                  <option value="">Any state</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Method">
              {(id) => (
                <Select id={id} value={methodFilter} onChange={(e) => setParam("method", e.target.value)}>
                  <option value="">Any method</option>
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {methodLabel(m)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {!versionId ? (
            <p className="muted-note">
              Select a framework version to propose a mapping or generate suggestions.{" "}
              <Link to="/studio/governance/frameworks">Browse frameworks</Link>
            </p>
          ) : null}
        </CardBody>
      </Card>

      <GovPanel
        title="Requirement mappings"
        state={mappings}
        loadingLabel="Loading mappings…"
        isEmpty={(d) => d.length === 0}
        emptyTitle="No mappings match"
        emptyDescription="Propose a mapping from a policy, or generate suggestions for a requirement."
      >
        {(rows) => (
          <div className="stack">
            {rows.map((m) => (
              <div key={m.id} className="gov-mapping">
                <div className="gov-mapping__head">
                  <div>
                    <strong>{requirementLabel(m.requirementId)}</strong>
                    <div className="muted-note">
                      {policyLabel(m.policyId ?? m.subjectId)} · {m.subjectType}
                    </div>
                  </div>
                  <div className="badge-row">
                    <MappingStateBadge state={m.state} method={m.method} />
                    <Badge>{methodLabel(m.method)}</Badge>
                    <Badge tone="neutral">{m.strength}</Badge>
                  </div>
                </div>

                {m.rationale ? <p>{m.rationale}</p> : null}

                {m.confidence !== null ? (
                  <p className="muted-note">
                    Model confidence {Math.round(m.confidence * 100)}% ({m.model ?? "unknown model"},
                    analyzer {m.analyzerVersion ?? "unknown"}). Informational only: confidence never affects
                    coverage or readiness.
                  </p>
                ) : null}

                {m.staleReason ? <p className="muted-note">Stale: {m.staleReason}</p> : null}

                {m.citations.length > 0 ? (
                  <ul className="gov-citations">
                    {m.citations.map((c, i) => (
                      <li key={`${c.sourceType}-${c.sourceId}-${i}`}>
                        <Badge tone="info">{c.sourceType}</Badge> <code>{c.sourceId}</code>
                        {c.excerpt ? <>: {c.excerpt}</> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="badge-row">
                  <Decisions orgId={orgId} mapping={m} onDone={mappings.reload} />
                  <Button variant="ghost" size="sm" onClick={() => setHistory(m)}>
                    History
                  </Button>
                  <span className="muted-note">updated {formatDate(m.updatedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </GovPanel>

      {history ? (
        <HistoryDialog
          orgId={orgId}
          mapping={history}
          label={requirementLabel(history.requirementId)}
          onClose={() => setHistory(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Only the transitions the backend can accept from the mapping's current state
 * are offered. It rejects a no-op decision with a 409, so offering "verify" on
 * an already-verified mapping would only produce a confusing error.
 */
function Decisions({
  orgId,
  mapping,
  onDone,
}: {
  orgId: string;
  mapping: RequirementMapping;
  onDone: () => void;
}) {
  const decide = (
    decision: "verified" | "rejected" | "reopened" | "marked_stale" | "superseded",
    rationale: string,
  ) => frameworksApi.decideMapping(orgId, mapping.id, decision, rationale || undefined);

  const verify = mapping.state !== "verified" && (
    <GovernedAction
      key="verify"
      permission={GOVERNANCE_PERMISSIONS.mappingsVerify}
      label="Verify"
      title="Verify this mapping"
      description="Records a human decision that this policy satisfies the requirement."
      consequence="Verification is what makes a mapping count towards coverage. The decision is appended to the mapping's immutable history."
      confirmLabel="Verify mapping"
      busyLabel="Verifying…"
      requireRationale
      rationaleHelp="Why this policy satisfies the requirement. Stored with the decision."
      triggerVariant="primary"
      onConfirm={(rationale) => decide("verified", rationale)}
      onDone={onDone}
    />
  );

  const reject = mapping.state !== "rejected" && (
    <GovernedAction
      key="reject"
      permission={GOVERNANCE_PERMISSIONS.mappingsVerify}
      label="Reject"
      title="Reject this mapping"
      description="Records that this policy does not satisfy the requirement."
      consequence="The mapping stops being a candidate and contributes nothing to coverage."
      confirmLabel="Reject mapping"
      busyLabel="Rejecting…"
      requireRationale
      destructive
      onConfirm={(rationale) => decide("rejected", rationale)}
      onDone={onDone}
    />
  );

  const reopen = mapping.state !== "proposed" && (
    <GovernedAction
      key="reopen"
      permission={GOVERNANCE_PERMISSIONS.mappingsVerify}
      label="Reopen"
      title="Reopen this mapping"
      description="Returns the mapping to proposed so it can be decided again."
      consequence="A reopened mapping no longer counts towards coverage until it is verified again."
      confirmLabel="Reopen"
      busyLabel="Reopening…"
      requireRationale
      onConfirm={(rationale) => decide("reopened", rationale)}
      onDone={onDone}
    />
  );

  const stale = mapping.state !== "stale" && (
    <GovernedAction
      key="stale"
      permission={GOVERNANCE_PERMISSIONS.mappingsVerify}
      label="Mark stale"
      title="Mark this mapping stale"
      description="Records that the mapping may no longer hold, usually because the policy or the requirement changed."
      consequence="A stale mapping stops counting towards coverage and appears in the stale gap list."
      confirmLabel="Mark stale"
      busyLabel="Marking…"
      requireRationale
      rationaleHelp="What changed. Shown on the mapping as the stale reason."
      onConfirm={(rationale) => decide("marked_stale", rationale)}
      onDone={onDone}
    />
  );

  const supersede = mapping.state !== "superseded" && (
    <GovernedAction
      key="supersede"
      permission={GOVERNANCE_PERMISSIONS.mappingsVerify}
      label="Supersede"
      title="Supersede this mapping"
      description="Records that another mapping replaces this one."
      consequence="Superseding is final: the mapping is kept for the audit trail but is no longer a live mapping."
      confirmLabel="Supersede"
      busyLabel="Superseding…"
      requireRationale
      destructive
      onConfirm={(rationale) => decide("superseded", rationale)}
      onDone={onDone}
    />
  );

  return <>{[verify, reject, reopen, stale, supersede].filter(Boolean)}</>;
}

function HistoryDialog({
  orgId,
  mapping,
  label,
  onClose,
}: {
  orgId: string;
  mapping: RequirementMapping;
  label: string;
  onClose: () => void;
}) {
  const state = useAsync(
    async () => (await frameworksApi.mappingHistory(orgId, mapping.id)).data.history,
    [orgId, mapping.id],
  );

  return (
    <Dialog
      open
      title="Decision history"
      description={`${label}: every decision recorded against this mapping, oldest first. History is only ever added to.`}
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <GovPanel
        state={state}
        bare
        loadingLabel="Loading history…"
        isEmpty={(d) => d.length === 0}
        emptyTitle="No decisions yet"
        emptyDescription="This mapping is still a proposal. Nobody has decided on it."
      >
        {(history) => (
          <ol className="gov-history">
            {history.map((h) => (
              <li key={h.id}>
                <Badge tone={h.decision === "verified" ? "success" : "neutral"}>{h.decision}</Badge>{" "}
                {h.fromState} → {h.toState}
                <div className="muted-note">
                  {formatDate(h.decidedAt)} · {h.actorId}
                </div>
                {h.rationale ? <p>{h.rationale}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </GovPanel>
    </Dialog>
  );
}

function CreateMapping({
  orgId,
  requirements,
  policies,
  initialRequirementId,
  onDone,
}: {
  orgId: string;
  requirements: FrameworkRequirement[];
  policies: Policy[];
  initialRequirementId: string;
  onDone: () => void;
}) {
  const [requirementId, setRequirementId] = useState(initialRequirementId);
  const [policyId, setPolicyId] = useState("");
  const [strength, setStrength] = useState<"full" | "partial" | "supporting">("full");
  const [rationale, setRationale] = useState("");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Map a policy"
      title="Propose a requirement mapping"
      description="The mapping is created as a proposal. It starts counting towards coverage only once someone verifies it."
      confirmLabel="Propose mapping"
      busyLabel="Proposing…"
      triggerVariant="primary"
      triggerSize="md"
      onConfirm={() =>
        frameworksApi.createMapping(orgId, {
          requirementId,
          subjectType: "policy",
          subjectId: policyId,
          policyId,
          strength,
          rationale: rationale.trim(),
        })
      }
      onDone={() => {
        setRationale("");
        onDone();
      }}
    >
      <Field label="Requirement">
        {(id) => (
          <Select id={id} value={requirementId} onChange={(e) => setRequirementId(e.target.value)}>
            <option value="">Select a requirement…</option>
            {requirements.map((r) => (
              <option key={r.id} value={r.id}>
                {r.requirementKey}: {r.title}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Policy">
        {(id) => (
          <Select id={id} value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
            <option value="">Select a policy…</option>
            {policies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Strength" help="How completely the policy satisfies the requirement.">
        {(id) => (
          <Select
            id={id}
            value={strength}
            onChange={(e) => setStrength(e.target.value as "full" | "partial" | "supporting")}
          >
            <option value="full">Full</option>
            <option value="partial">Partial</option>
            <option value="supporting">Supporting</option>
          </Select>
        )}
      </Field>
      <Field label="Rationale">
        {(id) => (
          <TextArea id={id} rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} />
        )}
      </Field>
    </GovernedAction>
  );
}

function SuggestMappings({
  orgId,
  requirements,
  initialRequirementId,
  onDone,
}: {
  orgId: string;
  requirements: FrameworkRequirement[];
  initialRequirementId: string;
  onDone: () => void;
}) {
  const [requirementId, setRequirementId] = useState(initialRequirementId);
  const [minConfidence, setMinConfidence] = useState("0.3");

  return (
    <GovernedAction
      permission={GOVERNANCE_PERMISSIONS.frameworksManage}
      label="Generate suggestions"
      title="Generate mapping suggestions"
      description="The backend proposes candidate policies for this requirement."
      consequence="Suggestions are stored as unverified proposals. They never count towards coverage until a person verifies them."
      confirmLabel="Generate"
      busyLabel="Generating…"
      triggerSize="md"
      onConfirm={() =>
        frameworksApi.suggestMappings(orgId, {
          requirementId,
          minConfidence: Number(minConfidence),
        })
      }
      onDone={onDone}
    >
      <Field label="Requirement">
        {(id) => (
          <Select id={id} value={requirementId} onChange={(e) => setRequirementId(e.target.value)}>
            <option value="">Select a requirement…</option>
            {requirements.map((r) => (
              <option key={r.id} value={r.id}>
                {r.requirementKey}: {r.title}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Minimum confidence" help="Filters the candidates the backend returns.">
        {(id) => (
          <Select id={id} value={minConfidence} onChange={(e) => setMinConfidence(e.target.value)}>
            <option value="0">Any</option>
            <option value="0.3">0.3</option>
            <option value="0.5">0.5</option>
            <option value="0.7">0.7</option>
          </Select>
        )}
      </Field>
    </GovernedAction>
  );
}
